import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Field } from '../../../components/Field';
import { Checkbox } from '../../../components/Checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import { cn } from '../../../lib/utils';
import { useShiftStore } from '../../../store/shiftStore';
import {
  MAX_RANGE_DAYS, WEEKDAYS, daysInRange, formatHours, fromKey, weekdayIndex,
} from './rosterDates';

const CLEAR = '__clear__';

export interface RosterEmployee { id: string; name: string; department: string }

export function BulkAssignDialog({ open, onOpenChange, employees, defaultFrom, defaultTo, preselected }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: RosterEmployee[];
  defaultFrom: string;
  defaultTo: string;
  /** Employee ids ticked when the dialog opens. */
  preselected?: string[];
}) {
  const { shifts, bulkAssign } = useShiftStore();
  const [shiftTypeId, setShiftTypeId] = useState('');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [weekdays, setWeekdays] = useState<boolean[]>([true, true, true, true, true, false, false]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFrom(defaultFrom);
    setTo(defaultTo);
    setShiftTypeId('');
    setWeekdays([true, true, true, true, true, false, false]);
    setSelected(new Set(preselected ?? []));
    setQuery('');
    setTouched(false);
  }, [open, defaultFrom, defaultTo, preselected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? employees.filter((e) => `${e.name} ${e.department}`.toLowerCase().includes(q)) : employees;
  }, [employees, query]);

  const rangeError = !from || !to ? 'Choose a start and end date'
    : from > to ? 'The end date is before the start date'
    : daysInRange(from, to).length > MAX_RANGE_DAYS ? `Choose ${MAX_RANGE_DAYS} days or fewer`
    : null;
  const days = rangeError ? [] : daysInRange(from, to).filter((d) => weekdays[weekdayIndex(d)]);
  const clearing = shiftTypeId === CLEAR;
  const shift = shifts.find((s) => s.id === shiftTypeId);
  const total = days.length * selected.size;

  const errors = {
    shift: !shiftTypeId ? 'Choose a shift' : null,
    range: rangeError ?? (days.length === 0 ? 'No days in this range match the selected weekdays' : null),
    employees: selected.size === 0 ? 'Select at least one employee' : null,
  };
  const valid = !errors.shift && !errors.range && !errors.employees;

  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));
  const someFilteredSelected = filtered.some((e) => selected.has(e.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filtered.forEach((e) => next.delete(e.id));
    else filtered.forEach((e) => next.add(e.id));
    setSelected(next);
  };
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    setSaving(true);
    try {
      const count = await bulkAssign([...selected], days, clearing ? null : shiftTypeId);
      toast.success(clearing
        ? `${count} ${count === 1 ? 'shift' : 'shifts'} cleared`
        : `${count} ${count === 1 ? 'shift' : 'shifts'} assigned`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const shortDate = (k: string) => fromKey(k).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign shifts</DialogTitle>
          <DialogDescription>
            Set one shift for several people and days at once. Existing shifts on those days are replaced.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-4">
            <Field label="Shift" required error={touched ? errors.shift ?? undefined : undefined}>
              <Select value={shiftTypeId || undefined} onValueChange={setShiftTypeId}>
                <SelectTrigger invalid={touched && !!errors.shift}>
                  <SelectValue placeholder="Choose a shift" />
                </SelectTrigger>
                <SelectContent>
                  {shifts.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="size-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name} · {s.startTime}–{s.endTime}
                      </span>
                    </SelectItem>
                  ))}
                  <SelectItem value={CLEAR}>Clear shifts (not scheduled)</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="From" htmlFor="bulk-from" required>
                <Input id="bulk-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="To" htmlFor="bulk-to" required>
                <Input id="bulk-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>

            <Field label="On these days" error={touched || rangeError ? errors.range ?? undefined : undefined}>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((w, i) => (
                  <button
                    key={w}
                    type="button"
                    aria-pressed={weekdays[i]}
                    onClick={() => setWeekdays(weekdays.map((v, j) => (j === i ? !v : v)))}
                    className={cn(
                      'h-8 w-11 rounded-md border text-sm font-medium transition-colors',
                      weekdays[i]
                        ? 'border-primary bg-primary/10 text-primary-strong'
                        : 'border-border bg-card text-muted-foreground hover:bg-secondary'
                    )}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </Field>

            <div className="rounded-lg border border-border bg-secondary/50 p-3 text-sm">
              {valid ? (
                <p className="text-foreground">
                  {clearing ? 'Clears' : <>Sets <span className="font-semibold">{shift?.name}</span> on</>}{' '}
                  <span className="font-semibold">{days.length}</span> {days.length === 1 ? 'day' : 'days'} for{' '}
                  <span className="font-semibold">{selected.size}</span> {selected.size === 1 ? 'person' : 'people'}
                  {' '}— {total} {total === 1 ? 'shift' : 'shifts'}
                  {!clearing && shift ? `, ${formatHours(total * shift.hours)} in total` : ''}.
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {shortDate(days[0])} to {shortDate(days[days.length - 1])}
                  </span>
                </p>
              ) : (
                <p className="text-muted-foreground">Choose a shift, dates and people to see a summary.</p>
              )}
            </div>
          </div>

          <Field label={`Employees · ${selected.size} selected`} error={touched ? errors.employees ?? undefined : undefined}>
            <div className="rounded-lg border border-border">
              <div className="border-b border-border p-2">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or department"
                  leftIcon={<Search className="size-4 text-muted-foreground" />}
                />
              </div>
              <label className="flex cursor-pointer items-center gap-3 border-b border-border px-3 py-2 text-sm font-medium">
                <Checkbox
                  checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAll}
                  disabled={filtered.length === 0}
                />
                {query ? `All ${filtered.length} matching` : `All ${filtered.length} employees`}
              </label>
              <ul className="max-h-64 overflow-y-auto">
                {filtered.length === 0 ? (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">No employees match</li>
                ) : filtered.map((e) => (
                  <li key={e.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-secondary/60">
                      <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggle(e.id)} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground">{e.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{e.department || 'No department'}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant={clearing ? 'destructive' : 'primary'} onClick={submit} isLoading={saving}>
            {clearing ? 'Clear shifts' : 'Assign shifts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
